import PrimaryButton from '@/components/Buttons/PrimaryButton';
import PageTitle from '@/components/PageTitle';
import ThemeWrapper from '@/components/ThemeWrapper';
import useThemeColors from '@/hooks/useThemeColors';
import AppLayout from '@/layouts/AppLayout';
import { useForm } from '@inertiajs/react';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface EnrollmentCertificateProps {
    errors?: {
        dni?: string;
        address?: string;
        non_field_errors?: string[];
    };
    csrfToken?: string;
}

const EnrollmentCertificateContent: React.FC<EnrollmentCertificateProps> = ({
    errors: serverErrors,
    csrfToken,
}) => {
    const { t } = useTranslation();
    const colors = useThemeColors();

    // Using useForm just for form state management, not for submission
    const { data, setData, errors } = useForm<{
        dni: string;
        address: string;
    }>({
        dni: '',
        address: '',
    });

    return (
        <AppLayout>
            <PageTitle title="Certificado de Matriculacion" />
            <div className="mx-auto max-w-3xl space-y-8">
                {/* Page Header */}
                <div className="space-y-4">
                    <h1 className="text-4xl font-bold text-gray-900 sm:text-5xl">
                        {t('enrollmentCertificate.title')}
                    </h1>
                    <p className="text-lg text-gray-700 sm:text-xl">
                        {t('enrollmentCertificate.description')}
                    </p>
                </div>

                {/* Form Card */}
                <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm sm:p-8">
                    {serverErrors?.non_field_errors && (
                        <div className="mb-6 rounded-lg bg-red-50 p-4 text-red-800">
                            {serverErrors.non_field_errors.join(', ')}
                        </div>
                    )}

                    <form
                        method="POST"
                        action="/enrollment-certificate/download/"
                        className="space-y-6"
                    >
                        <input
                            type="hidden"
                            name="csrfmiddlewaretoken"
                            value={csrfToken}
                        />
                        {/* DNI Field */}
                        <div className="space-y-2">
                            <label
                                htmlFor="dni"
                                className="block text-base font-medium text-gray-700"
                            >
                                {t('enrollmentCertificate.dniLabel')}
                            </label>
                            <input
                                type="text"
                                id="dni"
                                name="dni"
                                value={data.dni}
                                onChange={(e) => setData('dni', e.target.value)}
                                placeholder={t(
                                    'enrollmentCertificate.dniPlaceholder'
                                )}
                                className={`block w-full rounded-lg border border-gray-300 px-4 py-3 text-gray-900 placeholder-gray-400 focus:ring-2 focus:outline-none ${colors.inputFocus}`}
                            />
                            {(errors.dni || serverErrors?.dni) && (
                                <p className="text-base text-red-600">
                                    {errors.dni || serverErrors?.dni}
                                </p>
                            )}
                        </div>

                        {/* Address Field */}
                        <div className="space-y-2">
                            <label
                                htmlFor="address"
                                className="block text-base font-medium text-gray-700"
                            >
                                {t('enrollmentCertificate.addressLabel')}
                            </label>
                            <input
                                type="text"
                                id="address"
                                name="address"
                                value={data.address}
                                onChange={(e) =>
                                    setData('address', e.target.value)
                                }
                                placeholder={t(
                                    'enrollmentCertificate.addressPlaceholder'
                                )}
                                className={`block w-full rounded-lg border border-gray-300 px-4 py-3 text-gray-900 placeholder-gray-400 focus:ring-2 focus:outline-none ${colors.inputFocus}`}
                            />
                            {(errors.address || serverErrors?.address) && (
                                <p className="text-base text-red-600">
                                    {errors.address || serverErrors?.address}
                                </p>
                            )}
                        </div>

                        {/* Submit Button */}
                        <PrimaryButton type="submit" size="md">
                            {t('enrollmentCertificate.submitButton')}
                        </PrimaryButton>
                    </form>
                </div>
            </div>
        </AppLayout>
    );
};

const EnrollmentCertificate: React.FC<EnrollmentCertificateProps> = (props) => (
    <ThemeWrapper>
        <EnrollmentCertificateContent {...props} />
    </ThemeWrapper>
);

export default EnrollmentCertificate;
